import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Sheet from '@mui/joy/Sheet'
import IconButton from '@mui/joy/IconButton'
import Typography from '@mui/joy/Typography'
import CloseIcon from '@mui/icons-material/Close'
import JsonViewEditor from '@uiw/react-json-view/editor'
import JsonView from '@uiw/react-json-view'
import { ClientApplication } from '@numeo/types-core'
import { canEditApplicationJson } from 'utils/roles'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { useState, useEffect } from 'react'

interface ApplicationDetailsProps {
    open: boolean
    onClose: () => void
    application: ClientApplication | null
    onUpdate: (id: string) => Promise<void>
    onJsonChange: (edit: { updated_src: ClientApplication }) => void
    updating: boolean
}

export default function ApplicationDetails({ open, onClose, application, onUpdate, onJsonChange, updating }: ApplicationDetailsProps) {
    const [editedApplication, setEditedApplication] = useState<ClientApplication | null>(application)

    useEffect(() => {
        setEditedApplication(application)
    }, [application])

    if (!application || !editedApplication) return null

    const handleEdit = (opts: { value: unknown; oldValue: unknown; keyName?: string | number; parentName?: string | number; type?: 'key' | 'value' }) => {
        if (opts.keyName === undefined) return
        const updated = JSON.parse(JSON.stringify(editedApplication))
        const keys = String(opts.keyName).split('.')
        let current = updated
        for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]]
        }
        current[keys[keys.length - 1]] = opts.value
        setEditedApplication(updated)
        onJsonChange({ updated_src: updated })
    }

    return (
        <ClickAwayListener onClickAway={onClose}>
            <Sheet
                sx={{
                    position: 'fixed',
                    right: open ? '1.25rem' : '-31.25rem',
                    top: '5rem',
                    width: { xs: '100%', sm: '35rem' },
                    maxWidth: '40rem',
                    height: 'calc(100vh - 8rem)',
                    bgcolor: 'background.surface',
                    boxShadow: 'lg',
                    borderRadius: '0.5rem',
                    p: { xs: '1rem', sm: '1.5rem' },
                    transition: 'right 0.3s ease-in-out',
                    overflowY: 'auto',
                    zIndex: 1000,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '1.5rem' }}>
                    <Typography
                        level="h4"
                        component="h2"
                        sx={{
                            fontSize: { xs: 'md', sm: 'lg' },
                            fontWeight: 'lg',
                        }}
                    >
                        Application Details
                    </Typography>
                    <IconButton onClick={onClose} variant="plain" color="neutral" size="sm">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        mb: '1.5rem',
                        p: '1rem',
                        bgcolor: 'background.level1',
                        borderRadius: '0.5rem',
                    }}
                >
                    <Typography level="body-sm" sx={{ mb: 1, fontSize: 'sm' }}>
                        Project Name: {application.projectName}
                    </Typography>
                    <Typography level="body-sm" sx={{ mb: 1, fontSize: 'sm' }}>
                        Status: {application.status}
                    </Typography>
                    <Typography level="body-sm" sx={{ mb: 1, fontSize: 'sm' }}>
                        Created: {application.createdAt ? new Date(application.createdAt).toLocaleString() : 'N/A'}
                    </Typography>
                    <Typography level="body-sm" sx={{ mb: 1, fontSize: 'sm' }}>
                        Updated: {application.updatedAt ? new Date(application.updatedAt).toLocaleString() : 'N/A'}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        maxHeight: 'calc(100vh - 21.875rem)',
                        mb: '1.5rem',
                        '& > div': {
                            fontSize: '0.8125rem',
                        },
                    }}
                >
                    {canEditApplicationJson() ? (
                        <JsonViewEditor
                            value={editedApplication}
                            onEdit={handleEdit}
                            displayDataTypes={false}
                            collapsed={1}
                            style={{
                                backgroundColor: 'transparent',
                                padding: '8px',
                            }}
                        />
                    ) : (
                        <JsonView
                            value={editedApplication}
                            displayDataTypes={false}
                            collapsed={1}
                            style={{
                                backgroundColor: 'transparent',
                                padding: '8px',
                            }}
                        />
                    )}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        gap: '0.5rem',
                        justifyContent: 'flex-end',
                        position: 'sticky',
                        bottom: 0,
                        bgcolor: 'background.surface',
                        py: '1rem',
                    }}
                >
                    <Button size="sm" variant="outlined" color="neutral" onClick={onClose}>
                        {canEditApplicationJson() ? 'Cancel' : 'Close'}
                    </Button>
                    {canEditApplicationJson() && (
                        <Button size="sm" color="primary" onClick={() => onUpdate(application.id)} loading={updating} disabled={updating}>
                            Save Changes
                        </Button>
                    )}
                </Box>
            </Sheet>
        </ClickAwayListener>
    )
}
